import logging from "utils/logging";
import noopAdapter from "./adapters/noop";
import { validateEvent } from "./events";

/**
 * Analytics façade.
 *
 * The only surface the rest of the app imports. Delegates every call to the
 * registered AnalyticsAdapter (see src/analytics/contract.js). No component
 * should ever import a provider SDK directly.
 *
 * Usage:
 *   import Analytics from "analytics";
 *   import { EVENTS } from "analytics/events";
 *   Analytics.track(EVENTS.HOMEPAGE_CTA_CLICKED, { cta_location: "hero", destination: "/signup" });
 */

const log = logging.getLogger("analytics");
const isDev = process.env.NODE_ENV !== "production";

let adapter = noopAdapter;
let initialized = false;

const withValidation = (eventName, props) => {
  if (!isDev) return;
  const warnings = validateEvent(eventName, props);
  warnings.forEach((w) => log.warn(w));
};

const Analytics = {
  /**
   * Register the adapter to use. Call once at app bootstrap, before anything
   * else. If not called, the noop adapter stays in place.
   */
  register: (nextAdapter) => {
    if (!nextAdapter || typeof nextAdapter.track !== "function") {
      log.error("register: invalid adapter; keeping current", { nextAdapter });
      return;
    }
    adapter = nextAdapter;
    log.debug("adapter registered", { name: adapter.name });
  },

  init: async (apiKey, opts) => {
    if (initialized) {
      log.debug("init called twice, ignoring");
      return;
    }
    await adapter.init(apiKey, opts);
    initialized = true;
  },

  identify: (userId, traits) => adapter.identify(userId, traits),
  alias: (previousId, newId) => adapter.alias(previousId, newId),

  track: (eventName, props = {}) => {
    withValidation(eventName, props);
    adapter.track(eventName, props);
  },

  page: (path, props) => adapter.page(path, props),
  captureError: (error, ctx) => adapter.captureError(error, ctx),
  reset: () => adapter.reset(),
  setOptOut: (value) => adapter.setOptOut(value),

  /** Exposed for tests. */
  _getAdapterName: () => adapter.name,
};

export default Analytics;
